import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Venue } from '../../../types/venue';
import { theme } from '../../../theme';
import {
  formatPrice,
  formatVenueType,
  formatRating,
  getVenueAmenities,
} from '../../../utils/venueHelpers';
import { MapPin, Star, Users } from 'lucide-react';

interface VenueCardProps {
  venue: Venue;
}

const VenueCard: React.FC<VenueCardProps> = ({ venue }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/venues/${venue.id}`);
  };

  const amenities = getVenueAmenities(venue).slice(0, 3); // Show first 3 amenities

  const cardStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.card,
    overflow: 'hidden',
    boxShadow: theme.colors.shadow,
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  };

  const imageContainerStyle: React.CSSProperties = {
    width: '100%',
    height: '200px',
    overflow: 'hidden',
    position: 'relative',
    backgroundColor: theme.colors.background,
  };

  const imageStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  };

  const badgeStyle: React.CSSProperties = {
    position: 'absolute',
    top: '12px',
    right: '12px',
    backgroundColor: theme.colors.primary,
    color: '#000',
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: theme.fonts.sizeSmall,
    fontWeight: theme.fonts.weightBold,
  };

  const contentStyle: React.CSSProperties = {
    padding: '16px',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '1.1rem',
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
    marginBottom: '8px',
    fontFamily: theme.fonts.heading,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  };

  const locationStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: theme.fonts.sizeSmall,
    color: theme.colors.textSecondary,
    marginBottom: '12px',
  };

  const infoRowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '8px',
  };

  const ratingStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: theme.fonts.sizeSmall,
    color: theme.colors.text,
  };

  const capacityStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: theme.fonts.sizeSmall,
    color: theme.colors.textSecondary,
  };

  const amenitiesContainerStyle: React.CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    marginTop: '12px',
    marginBottom: '12px',
    flex: 1,
  };

  const amenityBadgeStyle: React.CSSProperties = {
    backgroundColor: theme.colors.background,
    color: theme.colors.textSecondary,
    padding: '4px 8px',
    borderRadius: '12px',
    fontSize: '0.75rem',
  };

  const priceContainerStyle: React.CSSProperties = {
    marginTop: 'auto',
    paddingTop: '12px',
    borderTop: `1px solid ${theme.colors.border}`,
  };

  const priceStyle: React.CSSProperties = {
    fontSize: '1.3rem',
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.primary,
  };

  const perHourStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeSmall,
    color: theme.colors.textSecondary,
    marginLeft: '4px',
  };

  // Get the first photo or use a placeholder
  const photoUrl = venue.photos && venue.photos.length > 0
    ? venue.photos[0].url
    : '/placeholder-venue.jpg';

  return (
    <div
      style={cardStyle}
      onClick={handleClick}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.3)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = theme.colors.shadow;
      }}
    >
      {/* Image */}
      <div style={imageContainerStyle}>
        <img src={photoUrl} alt={venue.name} style={imageStyle} />
        <div style={badgeStyle}>{formatVenueType(venue.venueType)}</div>
      </div>

      {/* Content */}
      <div style={contentStyle}>
        <h3 style={titleStyle}>{venue.name}</h3>

        <div style={locationStyle}>
          <MapPin size={14} />
          <span>{venue.city}, {venue.province}</span>
        </div>

        <div style={infoRowStyle}>
          <div style={ratingStyle}>
            <Star size={14} fill={theme.colors.primary} color={theme.colors.primary} />
            <span>
              {venue.averageRating ? formatRating(venue.averageRating) : 'Sin calificación'}
            </span>
            {venue.reviewCount && venue.reviewCount > 0 && (
              <span style={{ color: theme.colors.textSecondary }}>
                ({venue.reviewCount})
              </span>
            )}
          </div>

          <div style={capacityStyle}>
            <Users size={14} />
            <span>{venue.capacity} jugadores</span>
          </div>
        </div>

        {/* Amenities */}
        <div style={amenitiesContainerStyle}>
          {amenities.map((amenity, index) => (
            <span key={index} style={amenityBadgeStyle}>
              {amenity}
            </span>
          ))}
        </div>

        {/* Price */}
        <div style={priceContainerStyle}>
          <span style={priceStyle}>
            {formatPrice(venue.hourlyRate)}
            <span style={perHourStyle}>/hora</span>
          </span>
        </div>
      </div>
    </div>
  );
};

export default VenueCard;
